'use client';

import * as ENGINE from '@gnsx/genesys.js';

import { loadScene, saveScene } from '../sceneHandler';

import { editorProject, editorScene } from '@/editor/store/editorStore';
import { IS_DEV } from '@/lib/utils';


const DEBUG_LOG = {
  reloadWorld: true && IS_DEV,
} as const;

let reloadInProgress = false;

/**
 * Reload the current world without restarting the editor.
 * Saves the current scene when the editor world is available, then reloads it
 * to pick up any code/class changes. If Play Mode has temporarily unmounted
 * the editor world, reload from disk without saving to avoid serialising an
 * empty scene.
 *
 * Concurrent calls are dropped — only one reload runs at a time to prevent a
 * mid-reset `saveScene` from writing an empty scene file over the real data.
 *
 * Additional guards prevent data loss in unsafe states:
 * - Project must be open (prevents saving empty/null state).
 * - A world reset must not be in flight (mid-reset world is partial).
 * - Play mode must not be active or starting (editor world is hidden during PIE; saving it is unsafe).
 * - Missing/torn editor worlds are loaded from disk without saving first.
 *
 * @returns True if the reload was successful, false otherwise.
 */
export async function reloadCurrentWorld(): Promise<boolean> {
  if (reloadInProgress) {
    console.warn('[EditorActions] reloadCurrentWorld already in progress, skipping concurrent call');
    return false;
  }

  if (!editorProject.isProjectOpened) {
    console.warn('[EditorActions] reloadCurrentWorld skipped — project not open');
    return false;
  }

  if (editorScene.isResettingWorld) {
    console.warn('[EditorActions] reloadCurrentWorld skipped — world reset in progress');
    return false;
  }

  if (editorScene.isPlayMode || editorScene.isPlayModeStarting) {
    console.warn('[EditorActions] reloadCurrentWorld skipped — play mode is active (use externalRefresh to auto-restart)');
    return false;
  }

  if (!editorProject.currentScene) {
    console.warn('[EditorActions] No current scene to reload');
    return false;
  }

  reloadInProgress = true;
  const reloadStart = performance.now();
  try {
    console.log('🔄 [EditorActions] Reloading current world...');

    const currentScene = ENGINE.AssetPath.fromString(editorProject.currentScene);
    const canSaveBeforeReload = editorScene.world != null && editorScene.scene != null;

    if (canSaveBeforeReload) {
      await saveScene();
    } else {
      console.warn('[EditorActions] reloadCurrentWorld loading from disk because editor world is not ready');
    }

    // Load separately so a save failure does not proceed to an overwriting load.
    await loadScene(currentScene);

    if (DEBUG_LOG.reloadWorld) {
      console.log('[EditorActions] 🔄 Reload world took:', ((performance.now() - reloadStart) / 1000).toFixed(2), 's');
    }

    return true;
  } catch (error) {
    console.error('[EditorActions] Failed to reload world:', error);
    return false;
  } finally {
    reloadInProgress = false;
  }
}
